import type { TenantUsage } from '@yawi/types';
import { Card } from '@/core/ui/components';
import { TrendingUp, Users, Building, HardDrive, Bell } from 'lucide-react';

interface TenantUsageCardProps {
  usage?: TenantUsage | null;
}

function UsageBar({ label, percentage, used, limit, unit, icon }: {
  label: string;
  percentage: number;
  used: number;
  limit: number;
  unit?: string;
  icon: React.ReactNode;
}) {
  const getColor = (pct: number) => {
    if (pct >= 90) return 'bg-red-500';
    if (pct >= 70) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 text-theme-secondary">
          {icon}
          <span>{label}</span>
        </div>
        <span className="text-theme-tertiary">
          {used.toLocaleString()}{unit} / {limit.toLocaleString()}{unit}
        </span>
      </div>
      <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${getColor(percentage)}`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
      <div className="text-xs text-theme-tertiary text-right">{percentage}%</div>
    </div>
  );
}

export function TenantUsageCard({ usage }: TenantUsageCardProps) {
  if (!usage) {
    return (
      <Card>
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp className="w-5 h-5 text-theme-secondary" />
          <h3 className="text-lg font-semibold text-theme">Uso</h3>
        </div>
        <p className="text-theme-secondary text-center py-6">No hay datos de uso disponibles</p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-5 h-5 text-theme-secondary" />
          <h3 className="text-lg font-semibold text-theme">Uso del Periodo</h3>
        </div>
        <span className="text-sm text-theme-tertiary">{usage.currentPeriod}</span>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <UsageBar
          label="Usuarios"
          used={usage.activeUsers}
          limit={usage.limits.users}
          percentage={usage.percentages.users}
          icon={<Users className="w-4 h-4" />}
        />
        <UsageBar
          label="Unidades"
          used={usage.totalUnits}
          limit={usage.limits.units}
          percentage={usage.percentages.units}
          icon={<Building className="w-4 h-4" />}
        />
        <UsageBar
          label="Almacenamiento"
          used={Math.round(usage.storageBytes / (1024 * 1024 * 1024) * 10) / 10}
          limit={Math.round(usage.limits.storageBytes / (1024 * 1024 * 1024) * 10) / 10}
          percentage={usage.percentages.storage}
          unit=" GB"
          icon={<HardDrive className="w-4 h-4" />}
        />
        <UsageBar
          label="Notificaciones"
          used={usage.notificationsSent}
          limit={usage.limits.notifications}
          percentage={usage.percentages.notifications}
          icon={<Bell className="w-4 h-4" />}
        />
      </div>

      {(usage.overages.users > 0 || usage.overages.units > 0 || usage.overages.storageBytes > 0) && (
        <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
          <p className="text-sm text-amber-700 dark:text-amber-300">
            Este tenant tiene excedentes. Se aplicarán cargos adicionales.
          </p>
        </div>
      )}
    </Card>
  );
}
